import type { Directive } from 'vue'
import { nextTick } from 'vue'
import { useHighlightStore } from '@stores/useHighlightStore'

const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
type HighlightElement = HTMLElement & { __hlUnsub?: (() => void) | null }

const buildRegex = (terms: string[]): RegExp | null => {
  if (!terms || terms.length === 0) return null
  const parts = terms.map(t => escapeRegExp(t))
  return new RegExp(parts.join('|'), 'giu')
}

const unmark = (root: HTMLElement) => {
  const marks = root.querySelectorAll('mark')
  marks.forEach(m => {
    const parent = m.parentNode
    if (!parent) return
    parent.replaceChild(document.createTextNode(m.textContent || ''), m)
  })
}

const highlight = (root: HTMLElement, regex: RegExp) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = (node as Text).parentElement
      if (parent && parent.tagName === 'MARK') return NodeFilter.FILTER_REJECT
      const text = node.nodeValue || ''
      return text.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    }
  })

  const nodes: Text[] = []
  let n = walker.nextNode()
  while (n) {
    nodes.push(n as Text)
    n = walker.nextNode()
  }

  for (const node of nodes) {
    const text = node.nodeValue || ''
    const matches = [...text.matchAll(regex)]
    if (matches.length === 0) continue

    const frag = document.createDocumentFragment()
    let last = 0
    for (const m of matches) {
      const i = m.index ?? 0
      if (i > last) frag.appendChild(document.createTextNode(text.slice(last, i)))
      const mark = document.createElement('mark')
      mark.textContent = text.slice(i, i + m[0].length)
      frag.appendChild(mark)
      last = i + m[0].length
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)))
    node.parentNode?.replaceChild(frag, node)
  }
}

export const highlightDirective: Directive<HTMLElement, unknown> = {
  mounted(el) {
    const element = el as HighlightElement
    const store = useHighlightStore()

    const apply = () => {
      const regex = buildRegex(store.terms)
      if (!store.enabled || !regex) { unmark(el); return }
      unmark(el)
      highlight(el, regex)
    }

    // 初次应用
    nextTick(apply)

    // 订阅 store 变化（terms/enabled）以动态更新
    const unsubscribe = store.$subscribe(() => {
      nextTick(apply)
    })
    element.__hlUnsub = unsubscribe
  },
  updated(el) {
    const store = useHighlightStore()
    const apply = () => {
      const regex = buildRegex(store.terms)
      if (!store.enabled || !regex) { unmark(el); return }
      unmark(el)
      highlight(el, regex)
    }
    nextTick(apply)
  },
  beforeUnmount(el) {
    const element = el as HighlightElement
    const unsub = element.__hlUnsub
    if (typeof unsub === 'function') {
      unsub()
      element.__hlUnsub = null
    }
  }
}
